// @givmo/app-admin — combined Invite / Edit admin dialog (07-1.5b).
//
// One dialog, mode driven by the `adminToEdit` prop (ports the Flutter `EditOrInviteAdminPopUp`):
//   - INVITE (adminToEdit == null): title "Invite new admin", submit "Invite admin", shows the
//     "Work email" field and the role tiles, Global Admin by default. On submit fires
//     `companiesCreateCompanyAdmin` (POST /admin/company/admins) with a `CompanyAdminRegister` body
//     { first_name, last_name, email, role }; the Global Admin tile sends the wire's "Owner".
//   - EDIT (adminToEdit set): title "Edit admin", submit "Save", email HIDDEN, names pre-filled. Save =
//     ALWAYS the name PUT (`companiesUpdateCompanyAdminName`), then the role change ONLY when the
//     Global Admin switch moved (`companiesChangeCompanyAdminRole`, POST …/admins/{id}/role-change).
//
// ⚠ EDIT IS THE CONSOLE'S APPROVED DEMOTION DESIGN (D-EP-76 (3), the console's D-CPO-67), in its own
// words (`DEMOTION_COPY`, imported, never re-typed): the whole-company role is a Global Admin SWITCH,
// never Owner/Member tiles (Jonathan's definitions: Global Admin over the whole company; Owner and
// Member a level within one app).
//   * Turning it OFF on a Global Admin is a DEMOTION, which is a CHOICE (D-CPO-64's Also): every app
//     on the company's list, one that is off marked and still choosable, NOTHING pre-checked, and
//     Save off until at least one is on ("Choose the apps they’ll have…"), sent as `{role: "Member",
//     modules: [{module, role}]}`. Never the old role PUT, whose demotion kept three apps implicitly.
//   * Turning it ON on a regular admin promotes them, `{role: "Owner", modules: []}`. Their apps are
//     not edited here.
//   * Turning your OWN off: while another Global Admin is known or may exist, a warning
//     (`selfWarning`); when the roster, read AFRESH for this act, is whole and holds no one else at
//     that tier, the last one's line in the apps' place and Save off (`onlyGlobalAdmin`). Only an
//     agreeing fresh read blocks (Org Machinery's `469bae20`): a stale or failed read never holds back
//     a legal demotion, and the server's last-Owner guard is the backstop.
//   * A REFUSAL is said in the console's words (`roleChangeRefusalLine`), and leaves Save as a retry
//     deserves: off until the dialog is reopened after the 409 and the last Global Admin's refusal (no
//     choice of apps fixes either), off until the choice changes after a refusal of the choice itself.
//     The two that say the record moved under the dialog — the 409 and an app withdrawn — re-read the
//     roster at once and mark the apps' list stale WITHOUT re-reading it (a re-read would re-seat the
//     open choice the refusal is about), so the reopen their words ask for reads it afresh.
//
// Validation (verbatim copy): names match `^[a-zA-Z]+$` → "Please enter a valid name using only
// letters"; email format → "Invalid email format". Invite-only client domain enforcement: if the
// company has a non-empty `domain`, the email's domain must match (case-insensitively) or the submit
// is blocked with "Email domain must match company domain (<domain>)". A server 422/403 is surfaced
// inline too.
//
// DATA CONTRACT: `admins-edit-invite-*`, the invite tiles `invite-role-owner` / `invite-role-member`,
// and Edit's `edit-role-*` family: `-global-admin`, `-global-admin-notice`, `-self-demote-warning`,
// `-only-global-admin`, `-choose-apps` and the apps' `-apps` / `-app-<slug>-*` (`app-choice-fields`).

import { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { DEMOTION_COPY } from '@givmo/org-console';
import {
  choiceBody,
  emptyAppChoice,
  readChoiceRefusal,
  reseatAppChoice,
  sameChoiceBody,
  type AppChoiceBody,
  type AppChoiceDraft,
} from '@givmo/employer-module/features/admins/app-choice';
import {
  AppChoiceList,
  GlobalAdminCard,
  GlobalAdminNotice,
} from '@givmo/employer-module/features/admins/app-choice-fields';
import {
  APP_CHOICE_WORDS,
  roleChangeRefusalLine,
} from '@givmo/employer-module/features/admins/app-choice-words';
import {
  APP_CHOICES_KEY,
  useAppChoices,
} from '@givmo/employer-module/features/admins/use-app-choices';
import {
  sdk,
  isGivmoError,
  type AdminOut,
  type CompanyAdminMembershipType,
  type CompanyAdminRegister,
} from '@givmo/api-client';
import { companiesGetCompanyAdminQueryKey } from '@givmo/api-client/query';
import {
  Button,
  Dialog,
  ErrorMessage,
  Field,
  RadioTile,
  RadioTileGroup,
  testIdProp,
} from '@givmo/design-system';
import {
  EMAIL_REGEX,
  INVALID_EMAIL_MESSAGE,
  NAME_REGEX,
  INVALID_NAME_MESSAGE,
} from '../auth/auth-schemas';
import { ROLE_OWNER, ROLE_MEMBER, MEMBER_DESC, normalizeRole } from './admins-roles';

/** The Admin Team list as the page has loaded it: who else is a Global Admin, and how fresh. */
export interface AdminsRoster {
  admins: readonly AdminOut[];
  /** Every page is loaded, so the list can say no one else holds the tier. */
  complete: boolean;
  /** When the list last took an answer (the query's `dataUpdatedAt`). */
  updatedAt: number;
  fetching: boolean;
  refetch: () => void;
}

export interface AdminEditInviteDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** null → invite mode; an admin → edit mode. */
  adminToEdit: AdminOut | null;
  /** The company domain (CompanyDetailedOut.domain); '' disables the invite domain check. */
  companyDomain: string;
  /** The query key to invalidate on success (the admins infinite list). */
  invalidateKey: readonly unknown[];
  /** The page's own list, for the last Global Admin's line. */
  roster: AdminsRoster;
  /** The signed-in admin's id, `null` until known: who "you" are in the self-demotion lines. */
  selfUserId: string | null;
}

const GENERIC_FAILURE = 'Something went wrong. Please try again.';

export function AdminEditInviteDialog({
  open,
  onOpenChange,
  adminToEdit,
  companyDomain,
  invalidateKey,
  roster,
  selfUserId,
}: AdminEditInviteDialogProps) {
  const queryClient = useQueryClient();
  const isInvite = adminToEdit === null;

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  // Invite's tile choice; Edit carries the role as the Global Admin switch below.
  const [role, setRole] = useState<CompanyAdminMembershipType>(ROLE_OWNER);
  const [globalAdmin, setGlobalAdmin] = useState(false);
  const [apps, setApps] = useState<AppChoiceDraft>(() => emptyAppChoice([]));

  const [nameError, setNameError] = useState(false);
  const [emailError, setEmailError] = useState<string | null>(null);
  const [serverError, setServerError] = useState<string | null>(null);
  // A role-change refusal a retry cannot fix keeps Save off until the dialog is reopened; after a
  // refusal of the choice itself, Save returns once the choice changes.
  const [saveOffUntilReopen, setSaveOffUntilReopen] = useState(false);
  const [refusedChoice, setRefusedChoice] = useState<AppChoiceBody | null>(null);
  // When this opening asked for the roster afresh, for the last Global Admin's line.
  const [rosterAskedAt, setRosterAskedAt] = useState<number | null>(null);

  const wasGlobalAdmin =
    adminToEdit !== null && normalizeRole(adminToEdit.admin_company_role) === ROLE_OWNER;

  // (Re)prime the fields whenever the dialog opens OR the target admin changes — including reusing
  // the dialog for a different admin without closing. Invite defaults to Global Admin + blank fields;
  // edit pre-fills the names + the switch at the admin's current role.
  // ⚠ DURING RENDER, NEVER IN AN EFFECT (React's "adjusting state when a prop changes"). One render
  // with the last opening's switch would read as a demotion: it would start the apps' read and the
  // list's fresh read, and the last Global Admin's line would then rest on a read made before the
  // act, not for it.
  const primeKey = open ? (adminToEdit?.id ?? '') : null;
  const [primedKey, setPrimedKey] = useState<string | null>(null);
  if (primeKey !== primedKey) {
    setPrimedKey(primeKey);
    if (primeKey !== null) {
      setFirstName(adminToEdit?.first_name ?? '');
      setLastName(adminToEdit?.last_name ?? '');
      setEmail('');
      setRole(ROLE_OWNER);
      setGlobalAdmin(wasGlobalAdmin);
      setApps(emptyAppChoice([]));
      setNameError(false);
      setEmailError(null);
      setServerError(null);
      setSaveOffUntilReopen(false);
      setRefusedChoice(null);
      setRosterAskedAt(null);
    }
  }

  const isSelf =
    adminToEdit !== null &&
    selfUserId !== null &&
    selfUserId.length > 0 &&
    adminToEdit.id === selfUserId;
  const demoting = !isInvite && wasGlobalAdmin && !globalAdmin;
  const promoting = !isInvite && !wasGlobalAdmin && globalAdmin;
  const demotingSelf = isSelf && demoting;

  const choices = useAppChoices(open && demoting);
  useEffect(() => {
    if (!open || choices.rows === null) return;
    const rows = choices.rows;
    setApps((current) => reseatAppChoice(current, rows));
  }, [open, choices.rows]);
  const keptApps = demoting ? choiceBody(apps, { allowGlobalAdmin: false }) : null;

  // ⚠ A STALE ROSTER NEVER HOLDS BACK A LEGAL DEMOTION. When the loaded list says the person turning
  // their own Global Admin off is the only one, the dialog reads it afresh, once per opening, and the
  // last one's line holds Save off only on an answer that came back after that ask and agrees.
  const rosterSaysOnly =
    demotingSelf && isOnlyGlobalAdmin(roster.admins, selfUserId, roster.complete);
  const refetchRoster = roster.refetch;
  useEffect(() => {
    if (!rosterSaysOnly || rosterAskedAt !== null) return;
    setRosterAskedAt(Date.now());
    refetchRoster();
  }, [rosterSaysOnly, rosterAskedAt, refetchRoster]);
  const rosterFresh =
    rosterAskedAt !== null && roster.updatedAt >= rosterAskedAt && !roster.fetching;
  const onlyGlobalAdmin = rosterSaysOnly && rosterFresh;
  // While that read is out, nothing is said and nothing is offered: not the last one's line, and not
  // the warning and the apps it would replace.
  const checkingRoster =
    rosterSaysOnly && !rosterFresh && (rosterAskedAt === null || roster.fetching);

  const invite = useMutation({
    mutationFn: (body: CompanyAdminRegister) =>
      sdk.companiesCreateCompanyAdmin({ body, throwOnError: true }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: invalidateKey });
      onOpenChange(false);
    },
    onError: (error: unknown) => setServerError(serverMessage(error)),
  });

  const edit = useMutation({
    // Name first, then role — so a name success is durable even if the role call fails (partial
    // failure is surfaced, not swallowed).
    mutationFn: async (args: EditArgs) => {
      await sdk.companiesUpdateCompanyAdminName({
        path: { target_user_id: args.id },
        body: { new_first_name: args.firstName, new_last_name: args.lastName },
        throwOnError: true,
      });
      if (args.roleBody !== null) {
        try {
          await sdk.companiesChangeCompanyAdminRole({
            path: { target_user_id: args.id },
            body: args.roleBody,
            throwOnError: true,
          });
        } catch (error) {
          throw new RoleChangeFailure(error);
        }
      }
    },
    onSuccess: async (_data, args) => {
      // Re-read, never echo: an exact retry answers 200 with nothing changed, so the list says what
      // the server holds.
      await queryClient.invalidateQueries({ queryKey: invalidateKey });
      // A role change that landed may have been the signed-in admin's own (turning their own switch
      // off), so their standing read (`GET /admin/company/me`) is re-read too: the sidebar's account
      // card and this page's manage gate both read it, and neither may keep the old role (D-EP-77).
      // The generated key, never a string prefix: the factory's key is an object, which a string
      // never matches (the console's EP-SUITE3-FIX1).
      if (args.roleBody !== null) {
        await queryClient.invalidateQueries({ queryKey: companiesGetCompanyAdminQueryKey() });
      }
      onOpenChange(false);
    },
    onError: (error: unknown, args) => {
      if (!(error instanceof RoleChangeFailure)) {
        setServerError(serverMessage(error));
        return;
      }
      // The name saved before the role was refused, so the list re-reads either way.
      void queryClient.invalidateQueries({ queryKey: invalidateKey });
      const refusal = readChoiceRefusal(error.failure, 'ROLE_CHANGE', {
        demotingSelf: args.demotingSelf,
      });
      switch (refusal?.kind) {
        case 'last-global-admin':
        case 'not-global-admin':
          setSaveOffUntilReopen(true);
          break;
        case 'module-unavailable':
        case 'choice-required':
          setRefusedChoice(args.roleBody);
          break;
        default:
          break;
      }
      if (refusal?.kind === 'not-global-admin' || refusal?.kind === 'module-unavailable') {
        void queryClient.invalidateQueries({ queryKey: APP_CHOICES_KEY, refetchType: 'none' });
      }
      setServerError(roleChangeRefusalLine(refusal, args.who) ?? GENERIC_FAILURE);
    },
  });

  const submitting = invite.isPending || edit.isPending;

  // Save after a refusal a retry cannot fix (see `saveOffUntilReopen` above).
  const retryRefused =
    saveOffUntilReopen ||
    (demoting && refusedChoice !== null && sameChoiceBody(keptApps, refusedChoice));
  // The submit button is active only when both names are filled (and, in invite mode, the email); a
  // demotion also needs at least one app on, and neither the last one's line nor its fresh read.
  const buttonActive =
    firstName.trim().length > 0 &&
    lastName.trim().length > 0 &&
    (!isInvite || email.trim().length > 0) &&
    !retryRefused &&
    !onlyGlobalAdmin &&
    !checkingRoster &&
    (!demoting || keptApps !== null);

  const onSubmit = () => {
    setNameError(false);
    setEmailError(null);
    setServerError(null);

    const namesValid = NAME_REGEX.test(firstName) && NAME_REGEX.test(lastName);
    if (!namesValid) setNameError(true);

    let emailValid = true;
    if (isInvite) {
      if (!EMAIL_REGEX.test(email)) {
        setEmailError(INVALID_EMAIL_MESSAGE);
        emailValid = false;
      } else if (companyDomain.length > 0) {
        const emailDomain = email.split('@').pop()?.toLowerCase();
        if (emailDomain !== companyDomain.toLowerCase()) {
          setEmailError(`Email domain must match company domain (${companyDomain})`);
          emailValid = false;
        }
      }
    }

    if (!namesValid || !emailValid) return;

    if (isInvite) {
      invite.mutate({ first_name: firstName, last_name: lastName, email, role });
    } else {
      const roleBody: AppChoiceBody | null = demoting
        ? keptApps
        : promoting
          ? { role: ROLE_OWNER, modules: [] }
          : null;
      if (demoting && roleBody === null) return; // a demotion with nothing on: Save is held
      edit.mutate({
        id: adminToEdit.id,
        firstName,
        lastName,
        roleBody,
        demotingSelf,
        who: `${firstName.trim()} ${lastName.trim()}`,
      });
    }
  };

  return (
    <Dialog.Root open={open} onOpenChange={(next) => !submitting && onOpenChange(next)}>
      <Dialog.Content
        aria-label={isInvite ? 'Invite new admin' : 'Edit admin'}
        className="flex max-h-[80vh] flex-col gap-16 overflow-y-auto"
        testId="admins-edit-invite-dialog"
      >
        <Dialog.Title className="font-sans text-s18 font-w700 tracking-givmo text-text-strong">
          {isInvite ? 'Invite new admin' : 'Edit admin'}
        </Dialog.Title>

        <div className="flex gap-16">
          <div className="flex-1">
            <Field
              label="First name"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              testId="invite-firstname"
            />
          </div>
          <div className="flex-1">
            <Field
              label="Last name"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              testId="invite-lastname"
            />
          </div>
        </div>
        {nameError && (
          <ErrorMessage testId="admins-name-error">{INVALID_NAME_MESSAGE}</ErrorMessage>
        )}

        {isInvite && (
          <Field
            label="Work email"
            type="email"
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            testId="invite-email"
          />
        )}
        {emailError !== null && (
          <ErrorMessage testId="admins-email-error">{emailError}</ErrorMessage>
        )}

        {isInvite ? (
          <>
            <span className="font-sans text-s14 font-w600 tracking-givmo text-text-strong">
              Choose role
            </span>
            <RadioTileGroup
              value={role}
              onValueChange={(next) => setRole(next as CompanyAdminMembershipType)}
              aria-label="Choose role"
              className="gap-12"
              testId="admins-role-group"
            >
              <RadioTile
                value={ROLE_OWNER}
                label={APP_CHOICE_WORDS.globalAdmin}
                description={APP_CHOICE_WORDS.globalAdminDescription}
                testId="invite-role-owner"
              />
              <RadioTile
                value={ROLE_MEMBER}
                label={ROLE_MEMBER}
                description={MEMBER_DESC}
                testId="invite-role-member"
              />
            </RadioTileGroup>
          </>
        ) : (
          <>
            <GlobalAdminCard
              idPrefix="edit-role"
              checked={globalAdmin}
              onCheckedChange={setGlobalAdmin}
              disabled={submitting}
            />

            {/* A warning, not a block, while another Global Admin is known or may exist: the server's
                last-Owner guard is the backstop. The last one's own case is said in the apps' place,
                and while the roster is being read afresh for it, neither shows. */}
            {demotingSelf && !onlyGlobalAdmin && !checkingRoster && (
              <p
                className="font-sans text-s13 font-w500 leading-h15 tracking-givmo text-text-secondary"
                {...testIdProp('edit-role-self-demote-warning')}
              >
                {DEMOTION_COPY.selfWarning('company')}
              </p>
            )}

            {globalAdmin ? (
              <GlobalAdminNotice idPrefix="edit-role" />
            ) : !demoting ? null : onlyGlobalAdmin ? (
              // The roster is whole and nobody else is a Global Admin: no choice of apps can save, so
              // the list is not offered at all, and the line says the way out.
              <p
                className="rounded-lg bg-surface-sunken p-16 font-sans text-s13 font-w400 leading-h15 tracking-givmo text-text-secondary"
                {...testIdProp('edit-role-only-global-admin')}
              >
                {DEMOTION_COPY.onlyGlobalAdmin}
              </p>
            ) : checkingRoster ? null : (
              <AppChoiceList
                idPrefix="edit-role"
                draft={apps}
                onDraftChange={setApps}
                status={choices.status}
                onRetry={choices.refetch}
                disabled={submitting}
                lead={
                  <p
                    className="font-sans text-s13 font-w500 leading-h15 tracking-givmo text-text-secondary"
                    {...testIdProp('edit-role-choose-apps')}
                  >
                    {DEMOTION_COPY.chooseApps(isSelf)}
                  </p>
                }
              />
            )}
          </>
        )}

        {serverError !== null && (
          <ErrorMessage testId="admins-edit-invite-error">{serverError}</ErrorMessage>
        )}

        <div className="mt-8 flex items-center justify-end gap-8">
          <Dialog.Close
            disabled={submitting}
            render={<Button appearance="saas" tone="secondary" size="md" />}
            {...testIdProp('admins-edit-invite-cancel')}
          >
            Cancel
          </Dialog.Close>
          <Button
            appearance="saas"
            tone="primary"
            size="md"
            loading={submitting}
            disabled={!buttonActive}
            onClick={onSubmit}
            testId="admins-edit-invite-submit"
          >
            {isInvite ? 'Invite admin' : 'Save'}
          </Button>
        </div>
      </Dialog.Content>
    </Dialog.Root>
  );
}

interface EditArgs {
  id: string;
  firstName: string;
  lastName: string;
  roleBody: AppChoiceBody | null;
  /** Whether the role change takes the signed-in admin's own Global Admin away. */
  demotingSelf: boolean;
  /** The person being changed, for the 409's words. */
  who: string;
}

/**
 * Whether `selfUserId` is the company's only Global Admin, as far as the loaded list can say. Only a
 * COMPLETE list can say so: another Global Admin may sit on a page not loaded yet, so an incomplete one
 * answers `false`, and the server's last-Owner guard stays the backstop.
 */
function isOnlyGlobalAdmin(
  admins: readonly AdminOut[],
  selfUserId: string | null,
  complete: boolean,
): boolean {
  return (
    complete &&
    !admins.some(
      (admin) => admin.id !== selfUserId && normalizeRole(admin.admin_company_role) === ROLE_OWNER,
    )
  );
}

function serverMessage(error: unknown): string {
  return isGivmoError(error) ? error.message : GENERIC_FAILURE;
}

/** The role change's refusal, after the name saved: kept apart so its answer is read as the role's. */
class RoleChangeFailure extends Error {
  constructor(readonly failure: unknown) {
    super('The role change was refused.');
    this.name = 'RoleChangeFailure';
  }
}
